// T3-B PR 4-bis (2026-05-12):
//   template_motifs の create / edit。親 template_set 配下で create。
//
// Spec: design/sponsored-motif-spec.md v3 §6.8 / §4.2 / §F8

import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { Textarea } from '@/components/common/Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/Select';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle
} from '@/components/common/Sheet';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

interface Props {
    open: boolean,
    onOpenChange: (open: boolean) => void,
    mode: 'create' | 'edit',
    motif: any | null,
    // create 時に親 template_set_id を要求 (= URL に :id でぶら下がる API のため)
    parentTemplateSetId?: string | number,
    onSuccess?: () => void
}

const PROTECTED_SEED_SLUGS = ['whaleshark', 'turtle', 'yambaru_kuina', 'akashobin', 'mangrove'];

export default observer(function TemplateMotifFormSheet({ open, onOpenChange, mode, motif, parentTemplateSetId, onSuccess }: Props) {
    const {
        tierCatalogStore: {
            templateSets,
            tierPresets,
            listTemplateSets,
            listTierPresets,
            createTemplateMotif,
            updateTemplateMotif
        }
    } = useStore();

    const [templateSetId, setTemplateSetId] = useState<string>('');
    const [name, setName] = useState('');
    const [slug, setSlug] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [defaultTierValue, setDefaultTierValue] = useState<string>('1');
    const [defaultFortuneText, setDefaultFortuneText] = useState('');
    const [sponsorPurchaseDisabled, setSponsorPurchaseDisabled] = useState(false);
    const [postcardImageUrl, setPostcardImageUrl] = useState('');
    const [displayOrder, setDisplayOrder] = useState<string>('0');
    const [isActive, setIsActive] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        // dropdown 用 load
        flowResult(listTemplateSets());
        flowResult(listTierPresets());
        if (mode === 'edit' && motif) {
            setTemplateSetId(String(motif.template_set_id));
            setName(motif.name ?? '');
            setSlug(motif.slug ?? '');
            setImageUrl(motif.image_url ?? '');
            setDefaultTierValue(String(motif.default_tier_value ?? 1));
            setDefaultFortuneText(motif.default_fortune_text ?? '');
            setSponsorPurchaseDisabled(!!motif.sponsor_purchase_disabled);
            setPostcardImageUrl(motif.postcard_image_url ?? '');
            setDisplayOrder(String(motif.display_order ?? 0));
            setIsActive(!!motif.is_active);
        } else {
            setTemplateSetId(parentTemplateSetId ? String(parentTemplateSetId) : '');
            setName('');
            setSlug('');
            setImageUrl('');
            setDefaultTierValue('1');
            setDefaultFortuneText('');
            setSponsorPurchaseDisabled(false);
            setPostcardImageUrl('');
            setDisplayOrder('0');
            setIsActive(true);
        }
    }, [open, mode, motif, parentTemplateSetId]);

    const isSeedSlug = mode === 'edit' && PROTECTED_SEED_SLUGS.includes(motif?.slug);

    // 親 template_set → tier_preset → tier_definitions を引いて value 選択肢に
    const parentSet = templateSets.find(ts => Number(ts.id) === Number(templateSetId));
    const parentPreset = parentSet ? tierPresets.find(p => Number(p.id) === Number(parentSet.tier_preset_id)) : undefined;
    const tierValueOptions = parentPreset?.tier_definitions ?? [];

    const handleSubmit = async () => {
        if (!templateSetId) {
            toastify('error', '親テンプレートセットは必須');
            return;
        }
        if (!name.trim() || !slug.trim() || !imageUrl.trim() || !defaultTierValue) {
            toastify('error', '名称 / slug / image URL / tier value は必須');
            return;
        }
        const payload: any = {
            name,
            slug,
            image_url: imageUrl,
            default_tier_value: Number(defaultTierValue),
            default_fortune_text: defaultFortuneText || null,
            sponsor_purchase_disabled: sponsorPurchaseDisabled,
            postcard_image_url: postcardImageUrl || null,
            display_order: Number(displayOrder) || 0,
            is_active: isActive
        };
        setSubmitting(true);
        try {
            const res = mode === 'create' ?
                await flowResult(createTemplateMotif({ template_set_id: templateSetId, data: payload })) :
                await flowResult(updateTemplateMotif({ id: motif.id, data: payload }));
            if (res?.ok) {
                toastify('success', mode === 'create' ? '作成しました' : '更新しました');
                onOpenChange(false);
                onSuccess?.();
            } else {
                toastify('error', res?.message || '保存に失敗しました');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className='w-[600px] sm:max-w-[600px] overflow-y-auto'>
                <SheetHeader>
                    <SheetTitle>{mode === 'create' ? 'テンプレ motif 新規作成' : 'テンプレ motif 編集'}</SheetTitle>
                    <SheetDescription>
                        親テンプレートセット配下の motif。omikuji_texts.motif slug と整合させる。
                    </SheetDescription>
                </SheetHeader>

                <div className='space-y-4 py-4'>
                    <div>
                        <Label>
                            親テンプレートセット
                            <span className='text-red-500'>*</span>
                        </Label>
                        <Select value={templateSetId} onValueChange={setTemplateSetId} disabled={mode === 'edit'}>
                            <SelectTrigger>
                                <SelectValue placeholder='選択してください' />
                            </SelectTrigger>
                            <SelectContent>
                                {templateSets.map(ts => (
                                    <SelectItem key={ts.id} value={String(ts.id)}>
                                        {ts.name}
                                        {' '}
                                        (
                                        {ts.slug}
                                        )
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {mode === 'edit' && <p className='text-xs text-gray-500 mt-1'>編集時の親変更は不可</p>}
                    </div>

                    <div>
                        <Label>
                            名称
                            <span className='text-red-500'>*</span>
                        </Label>
                        <Input value={name} onChange={e => setName(e.target.value)} placeholder='例: ジンベエザメ' />
                    </div>

                    <div>
                        <Label>
                            モチーフID(slug)
                            <span className='text-red-500'>*</span>
                        </Label>
                        <Input
                            value={slug}
                            onChange={e => setSlug(e.target.value)}
                            placeholder='例: whaleshark'
                            disabled={isSeedSlug}
                        />
                        {isSeedSlug && <p className='text-xs text-amber-700 mt-1'>両替機 PoC seed slug は rename 不可</p>}
                        <p className='text-xs text-gray-500 mt-1'>絵柄を識別する英数字IDです。占い文設定(motif)と同じ文字列にすると、その占い文にこの絵柄が使われます。 例: whaleshark</p>
                    </div>

                    <div>
                        <Label>
                            画像 URL
                            <span className='text-red-500'>*</span>
                        </Label>
                        <Input value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder='/postcards/whaleshark.png or https://...' />
                    </div>

                    <div>
                        <Label>
                            default tier value
                            <span className='text-red-500'>*</span>
                        </Label>
                        {tierValueOptions.length > 0 ?
                            (
                                <Select value={defaultTierValue} onValueChange={setDefaultTierValue}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {tierValueOptions.map(d => (
                                            <SelectItem key={d.value} value={String(d.value)}>
                                                {d.value}
                                                :
                                                {d.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) :
                            (
                                <Input type='number' value={defaultTierValue} onChange={e => setDefaultTierValue(e.target.value)} />
                            )}
                        <p className='text-xs text-gray-500 mt-1'>親 tier preset の tier_definitions の value</p>
                    </div>

                    <div>
                        <Label>default 占い文</Label>
                        <Textarea value={defaultFortuneText} onChange={e => setDefaultFortuneText(e.target.value)} rows={2} placeholder='omikuji_texts に未登録時の fallback (任意)' />
                    </div>

                    <div>
                        <Label>postcard 画像 URL (= 物理化用)</Label>
                        <Input value={postcardImageUrl} onChange={e => setPostcardImageUrl(e.target.value)} placeholder='未指定なら image_url 流用' />
                    </div>

                    <div className='grid grid-cols-2 gap-4'>
                        <div>
                            <Label>display_order</Label>
                            <Input type='number' value={displayOrder} onChange={e => setDisplayOrder(e.target.value)} />
                        </div>
                    </div>

                    <div className='flex items-center gap-2'>
                        <input
                            type='checkbox'
                            checked={sponsorPurchaseDisabled}
                            onChange={e => setSponsorPurchaseDisabled(e.target.checked)}
                            id='sponsor_disabled'
                        />
                        <Label htmlFor='sponsor_disabled'>sponsor_purchase_disabled (= sponsor 化禁止、両替機 PoC 5 種 default true)</Label>
                    </div>

                    <div className='flex items-center gap-2'>
                        <input
                            type='checkbox'
                            checked={isActive}
                            onChange={e => setIsActive(e.target.checked)}
                            id='tm_is_active'
                        />
                        <Label htmlFor='tm_is_active'>is_active (= 排出 / dropdown 表示)</Label>
                    </div>
                </div>

                <SheetFooter>
                    <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                        キャンセル
                    </Button>
                    <Button type='button' variant='active' onClick={handleSubmit} disabled={submitting}>
                        {mode === 'create' ? '作成' : '更新'}
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
});
